import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Checkbox, Divider } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { ArrowDown, ArrowUp } from '@/components/MapGeneration/Modals/VersionsModal/icons';
import { setLayerSettings } from '@/components/MapGeneration/store/actions';
import { InputNumber } from '@/shared/MiInputNumber/MiInputNumber';
import { state } from '../../../store/state';

const CheckboxGroup = Checkbox.Group;

export const WellSignsSettings = ({ layer }) => {
    const $state = useSnapshot(state);

    const firstRenderRef = useRef(false);

    useEffect(() => {
        if (firstRenderRef.current) return;
        if (!state.layerSettings.has(layer.guid)) return;

        setTitleVisible(state.layerSettings.get(layer.guid)?.titleVisible);
        setTitleScaleFactor(state.layerSettings.get(layer.guid)?.titleScaleFactor);

        wellBoreType_setCheckedList(
            state.layerSettings.get(layer.guid)?.wellBoreTypeIds.map((item) => {
                return state.wellBoreType?.find((obj) => obj.id === item)?.title;
            }),
        );

        wellBoreGeometry_setCheckedList(
            state.layerSettings.get(layer.guid)?.wellBoreGeometryTypeIds.map((item) => {
                return state.wellBoreGeometry?.find((obj) => obj.id === item)?.title;
            }),
        );

        wellBuildType_setCheckedList(
            state.layerSettings.get(layer.guid)?.wellPositionVersionPriority.map((item) => {
                return state.wellBuildType?.find((obj) => obj.id === item)?.title;
            }),
        );

        firstRenderRef.current = true;
    }, [layer, $state.layerSettings]);

    const [titleVisible, setTitleVisible] = useState(true);
    const [titleScaleFactor, setTitleScaleFactor] = useState(1);

    // ---

    const [wellBoreType_checkedList, wellBoreType_setCheckedList] = useState([]);
    const [wellBoreType_indeterminate, wellBoreType_setIndeterminate] = useState(true);
    const [wellBoreType_checkAll, wellBoreType_setCheckAll] = useState(false);
    const wellBoreType_onChange = (list) => {
        wellBoreType_setCheckedList(list);
        wellBoreType_setIndeterminate(
            !!list.length && list.length < state?.wellBoreType.map((item) => item.title).length,
        );
        wellBoreType_setCheckAll(list.length === state?.wellBoreType.map((item) => item.title).length);

        const currentSettings = state.layerSettings.get(layer.guid);

        const settings = {
            ...currentSettings,
            wellBoreTypeIds: state.wellBoreType.flatMap((item) => {
                const ids = [];
                if (list.includes(item.title)) {
                    ids.push(item.id);
                }
                return ids;
            }),
        };
        setLayerSettings(layer, settings);
    };
    const wellBoreType_onCheckAllChange = (e) => {
        wellBoreType_setCheckedList(e.target.checked ? state?.wellBoreType.map((item) => item.title) : []);
        wellBoreType_setIndeterminate(false);
        wellBoreType_setCheckAll(e.target.checked);
    };

    // ---

    const [wellBoreGeometry_checkedList, wellBoreGeometry_setCheckedList] = useState([]);
    const [wellBoreGeometry_indeterminate, wellBoreGeometry_setIndeterminate] = useState(true);
    const [wellBoreGeometry_checkAll, wellBoreGeometry_setCheckAll] = useState(false);
    const wellBoreGeometry_onChange = (list) => {
        wellBoreGeometry_setCheckedList(list);
        wellBoreGeometry_setIndeterminate(
            !!list.length && list.length < state?.wellBoreGeometry.map((item) => item.title).length,
        );
        wellBoreGeometry_setCheckAll(list.length === state?.wellBoreGeometry.map((item) => item.title).length);

        const currentSettings = state.layerSettings.get(layer.guid);

        const settings = {
            ...currentSettings,
            wellBoreGeometryTypeIds: state.wellBoreGeometry.flatMap((item) => {
                const ids = [];
                if (list.includes(item.title)) {
                    ids.push(item.id);
                }
                return ids;
            }),
        };
        setLayerSettings(layer, settings);
    };
    const wellBoreGeometry_onCheckAllChange = (e) => {
        wellBoreGeometry_setCheckedList(e.target.checked ? state?.wellBoreGeometry.map((item) => item.title) : []);
        wellBoreGeometry_setIndeterminate(false);
        wellBoreGeometry_setCheckAll(e.target.checked);
    };

    // ---

    const [wellBuildType_checkedList, wellBuildType_setCheckedList] = useState([]);
    const [wellBuildType_indeterminate, wellBuildType_setIndeterminate] = useState(true);
    const [wellBuildType_checkAll, wellBuildType_setCheckAll] = useState(false);
    const wellBuildType_onChange = (list) => {
        wellBuildType_setCheckedList(list);
        wellBuildType_setIndeterminate(
            !!list.length && list.length < state?.wellBuildType.map((item) => item.title).length,
        );
        wellBuildType_setCheckAll(list.length === state?.wellBuildType.map((item) => item.title).length);

        const currentSettings = state.layerSettings.get(layer.guid);

        const settings = {
            ...currentSettings,
            wellPositionVersionPriority: state.wellBuildType.flatMap((item) => {
                const ids = [];
                if (list.includes(item.title)) {
                    ids.push(item.id);
                }
                return ids;
            }),
        };
        setLayerSettings(layer, settings);
    };
    const wellBuildType_onCheckAllChange = (e) => {
        wellBuildType_setCheckedList(e.target.checked ? state?.wellBuildType.map((item) => item.title) : []);
        wellBuildType_setIndeterminate(false);
        wellBuildType_setCheckAll(e.target.checked);
    };

    const moveUp = (currentIndex) => (item) => () => {
        const oldState = [...$state.wellBuildType];
        const prevItem = oldState[currentIndex - 1];
        oldState[currentIndex - 1] = item;
        oldState[currentIndex] = prevItem;
        state.wellBuildType = oldState;
        wellBuildType_onChange(
            oldState.filter((i) => wellBuildType_checkedList.includes(i.title)).map((item) => item.title),
        );
    };

    const moveDown = (currentIndex) => (item) => () => {
        const oldState = [...$state.wellBuildType];
        const nextItem = oldState[currentIndex + 1];
        oldState[currentIndex + 1] = item;
        oldState[currentIndex] = nextItem;
        state.wellBuildType = oldState;
        wellBuildType_onChange(
            oldState.filter((i) => wellBuildType_checkedList.includes(i.title)).map((item) => item.title),
        );
    };

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden border-t border-gray-500 dark:border-fill-primary">
            <div className="flex w-full flex-1 min-h-0 flex-col overflow-y-auto bg-white py-2 pb-2 dark:bg-block-primary">
                <div className="my-1 flex items-center justify-between px-3">
                    <Checkbox
                        className="ui-checkbox"
                        value={titleVisible}
                        checked={titleVisible}
                        onChange={(e) => {
                            setTitleVisible(e.target.checked);
                            const currentSettings = state.layerSettings.get(layer.guid);
                            const settings = {
                                ...currentSettings,
                                titleVisible: e.target.checked,
                            };
                            setLayerSettings(layer, settings);
                        }}
                    >
                        Подписи скважин
                    </Checkbox>
                    <div className="flex items-center">
                        <InputNumber
                            className="w-12"
                            value={titleScaleFactor}
                            onChange={(value) => {
                                setTitleScaleFactor(value);
                                const currentSettings = state.layerSettings.get(layer.guid);
                                const settings = {
                                    ...currentSettings,
                                    titleScaleFactor: value,
                                };
                                setLayerSettings(layer, settings);
                            }}
                            size="small"
                            step={1}
                            min={1}
                            max={30}
                        />
                    </div>
                </div>

                <Divider className="!my-2" />

                <div className="px-3">
                    {/* <Checkbox
                        className="ui-checkbox"
                        indeterminate={wellBoreType_indeterminate}
                        onChange={wellBoreType_onCheckAllChange}
                        checked={wellBoreType_checkAll}
                        disabled
                    >
                        Типы стволов
                    </Checkbox> */}
                    <span className="text-xs text-black dark:text-font-secondary">Типы стволов</span>
                </div>
                <Divider className="!my-2" />
                <div className="px-3">
                    <CheckboxGroup
                        className="ui-checkbox flex flex-col"
                        options={$state?.wellBoreType.map((item) => item.title)}
                        defaultValue={$state?.wellBoreType.map((item) => item.title)}
                        value={wellBoreType_checkedList}
                        onChange={(e) => wellBoreType_onChange(e)}
                    />
                </div>

                <Divider className="!my-2" />

                <div className="px-3">
                    {/* <Checkbox
                        className="ui-checkbox"
                        indeterminate={wellBoreGeometry_indeterminate}
                        onChange={wellBoreGeometry_onCheckAllChange}
                        checked={wellBoreGeometry_checkAll}
                        disabled
                    >
                        Геометрия ствола
                    </Checkbox> */}
                    <span className="text-xs text-black dark:text-font-secondary">Геометрия ствола</span>
                </div>
                <Divider className="!my-2" />
                <div className="px-3">
                    <CheckboxGroup
                        className="ui-checkbox flex flex-col"
                        options={$state?.wellBoreGeometry.map((item) => item.title)}
                        defaultValue={$state?.wellBoreGeometry.map((item) => item.title)}
                        value={wellBoreGeometry_checkedList}
                        onChange={(e) => wellBoreGeometry_onChange(e)}
                    />
                </div>

                <Divider className="!my-2" />

                <div className="px-3">
                    {/* <Checkbox
                        className="ui-checkbox"
                        indeterminate={wellBuildType_indeterminate}
                        onChange={wellBuildType_onCheckAllChange}
                        checked={wellBuildType_checkAll}
                        disabled
                    >
                        Приоритет типов построения
                    </Checkbox> */}
                    <span className="text-xs text-black dark:text-font-secondary">Приоритет типов построения</span>
                </div>

                <Divider className="!my-2" />

                <div className="px-3 pb-2">
                    <CheckboxGroup
                        className="ui-checkbox flex flex-col"
                        defaultValue={$state?.wellBuildType.map((item) => item.title)}
                        value={wellBuildType_checkedList}
                        onChange={(e) => wellBuildType_onChange(e)}
                    >
                        {$state?.wellBuildType.map((item, index, array) => (
                            <div
                                id="checkbox-custom-order"
                                className="flex items-center justify-between"
                                key={`${item.title}-${index}`}
                            >
                                <Checkbox className="ui-checkbox" value={item.title}>
                                    {item.title}
                                </Checkbox>
                                <div className="order-handlers-wrapper flex flex-col items-center justify-center">
                                    {index !== 0 ? (
                                        <ArrowUp
                                            className="h-4 w-4 cursor-pointer fill-fill-primary dark:fill-slate-800"
                                            onClick={moveUp(index)(item)}
                                        />
                                    ) : null}
                                    {index !== array.length - 1 ? (
                                        <ArrowDown
                                            className="h-4 w-4 cursor-pointer fill-fill-primary dark:fill-slate-800"
                                            onClick={moveDown(index)(item)}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </CheckboxGroup>
                </div>
            </div>
        </div>
    );
};
